import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import uz from "@/locales/uz.json";
import ru from "@/locales/ru.json";
import en from "@/locales/en.json";

// Per-screen translation parts: locales/parts/<group>.<lang>.json, each with its own unique top-level namespaces.
const partModules = import.meta.glob<Record<string, unknown>>("../locales/parts/*.json", { eager: true, import: "default" });

function loadParts(lang: string): Record<string, unknown> {
  const suffix = `.${lang}.json`;
  return Object.assign(
    {},
    ...Object.entries(partModules)
      .filter(([path]) => path.endsWith(suffix))
      .map(([, mod]) => mod),
  );
}

export const SUPPORTED_LANGUAGES = ["uz", "ru", "en"] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = "hisobpos.language";
const DEFAULT_LANGUAGE: SupportedLanguage = "uz";

function isSupportedLanguage(value: string | null): value is SupportedLanguage {
  return value !== null && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

function readStoredLanguage(): SupportedLanguage {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return isSupportedLanguage(stored) ? stored : DEFAULT_LANGUAGE;
  } catch {
    return DEFAULT_LANGUAGE;
  }
}

void i18n.use(initReactI18next).init({
  resources: {
    uz: { translation: { ...uz, ...loadParts("uz") } },
    ru: { translation: { ...ru, ...loadParts("ru") } },
    en: { translation: { ...en, ...loadParts("en") } },
  },
  lng: readStoredLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: { escapeValue: false },
});

// A UI language preference, not an auth token — safe to persist in localStorage (FR-53 only forbids that for sessions).
i18n.on("languageChanged", (lng) => {
  try {
    localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    // localStorage unavailable (private mode, etc.) — language just won't persist across reloads.
  }
});

export default i18n;
